import { useEffect, useRef, useState } from 'react';

import { getDeploymentStatus } from '@/shared/api/deploy';
import {
  DEPLOYMENT_STATUS,
  type DeploymentChange,
  type DeploymentChangesEvent,
  type DeploymentDetailResponse,
  type DeploymentStatus,
} from '@/shared/api/deploy/types';

const IS_DEMO_MODE = import.meta.env.MODE === 'demo';

type UseDeploymentEventsOptions = {
  onStatusChange?: (status: DeploymentStatus, change: DeploymentChange) => void;
  onError?: (error: Error) => void;
  pollSeconds?: number;
  heartbeatSeconds?: number;
  enabled?: boolean;
};

export const useDeploymentEvents = (deploymentId: string | null, options: UseDeploymentEventsOptions = {}) => {
  const { onStatusChange, onError, pollSeconds = 2, heartbeatSeconds = 15, enabled = true } = options;

  const [status, setStatus] = useState<DeploymentStatus | null>(null);
  const [lastUpdate, setLastUpdate] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isTerminated, setIsTerminated] = useState(false);

  const eventSourceRef = useRef<EventSource | null>(null);
  const onStatusChangeRef = useRef(onStatusChange);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onStatusChangeRef.current = onStatusChange;
    onErrorRef.current = onError;
  }, [onStatusChange, onError]);

  useEffect(() => {
    if (!deploymentId) {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      setIsConnected(false);
      setIsTerminated(false);
      return;
    }

    if (!enabled) {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      setIsConnected(false);
      // 터미널(serving_active 등) 이후에도 다음에 enabled가 켜지면 SSE를 다시 열 수 있게
      setIsTerminated(false);
      return;
    }

    if (isTerminated) {
      return;
    }

    if (eventSourceRef.current) {
      return;
    }

    const isTerminalChange = (status: DeploymentStatus, change: Pick<DeploymentChange, 'last_error_summary'>) => {
      const hasError = !!change.last_error_summary;

      return (
        status === DEPLOYMENT_STATUS.SERVING_ACTIVE ||
        status === DEPLOYMENT_STATUS.DEPLOYMENT_FAILED ||
        status === DEPLOYMENT_STATUS.SERVING_FAILED ||
        (status === DEPLOYMENT_STATUS.SERVING_INACTIVE && hasError)
      );
    };

    const toDeploymentChange = (detail: DeploymentDetailResponse): DeploymentChange => ({
      deployment_id: detail.deployment_id,
      deployment_name: detail.deployment_name,
      status: detail.status,
      replicas: detail.replicas,
      deployed_at: detail.deployed_at ?? null,
      deleted_at: detail.deleted_at ?? null,
      updated_at: detail.updated_at ?? new Date().toISOString(),
      hpa: detail.metadata?.hpa ?? {
        enabled: false,
        min_replicas: null,
        max_replicas: null,
        target_cpu_utilization: null,
        target_memory_utilization: null,
      },
      last_error_summary: null,
    });

    if (IS_DEMO_MODE) {
      let cancelled = false;

      const pollDeploymentStatus = async () => {
        try {
          const detail = await getDeploymentStatus(deploymentId);
          if (cancelled) return;

          const change = toDeploymentChange(detail);

          setIsConnected(true);
          setError(null);
          setStatus(change.status);
          setLastUpdate(change.updated_at);

          onStatusChangeRef.current?.(change.status, change);

          if (isTerminalChange(change.status, change)) {
            setIsTerminated(true);
            setIsConnected(false);
          }
        } catch (error) {
          if (cancelled) return;

          setIsConnected(false);
          const errorObj = error instanceof Error ? error : new Error('Demo deployment polling failed');
          setError(errorObj);
          onErrorRef.current?.(errorObj);
        }
      };

      void pollDeploymentStatus();
      const interval = setInterval(pollDeploymentStatus, pollSeconds * 1000);

      return () => {
        cancelled = true;
        clearInterval(interval);
        setIsConnected(false);
      };
    }

    const url = `/deploy/api/v1/deployments/${deploymentId}/events?poll_seconds=${pollSeconds}&heartbeat_seconds=${heartbeatSeconds}`;

    const eventSource = new EventSource(url, {
      withCredentials: true,
    });

    eventSourceRef.current = eventSource;

    eventSource.onopen = () => {
      setIsConnected(true);
      setError(null);
    };

    eventSource.addEventListener('deployment_changes', (event) => {
      try {
        const data: DeploymentChangesEvent = JSON.parse(event.data);

        if (data.changes && data.changes.length > 0) {
          const latestChange = data.changes[0];

          const isTerminalStatus = isTerminalChange(latestChange.status, latestChange);

          setStatus(latestChange.status);
          setLastUpdate(latestChange.updated_at);

          if (onStatusChangeRef.current) {
            try {
              onStatusChangeRef.current(latestChange.status, latestChange);
            } catch (error) {
              console.error('[SSE] Error in onStatusChange callback:', error);
            }
          }

          if (isTerminalStatus) {
            setIsTerminated(true);
            eventSource.close();
            setIsConnected(false);
          }
        }
      } catch (error) {
        console.error('[SSE] Failed to parse deployment_changes:', error);
      }
    });

    eventSource.addEventListener('heartbeat', () => {});

    eventSource.onerror = () => {
      setIsConnected(false);
      const errorObj = new Error('SSE connection failed');
      setError(errorObj);

      if (onErrorRef.current) {
        onErrorRef.current(errorObj);
      }
    };

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      setIsConnected(false);
    };
  }, [deploymentId, isTerminated, enabled, pollSeconds, heartbeatSeconds]);

  return {
    status,
    lastUpdate,
    isConnected,
    error,
  };
};
